import { HttpClient, HttpEvent, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { LoadingController } from "@ionic/angular";
import { TranslocoService } from "@ngneat/transloco";
import { map, timeout, switchMap, take, filter } from "rxjs/operators";
import { EnvironmentQuery } from "../state/environment.query";
import { LocationStateQuery } from "../state/location-state.query";
import { Location } from "../state/location-state.store";
import { combineLatest, Observable } from "rxjs";
import { Checklist, LocationActivity } from "source/app/features/checklists/checklist.interface";
import { AssignmentDefinitionSettings } from "source/app/features/assignments/assignment.interface";
import { Note } from "../state/note.model";
import { UiQuery } from "../state/ui.query";
import { ServerStateResponse } from "../state/location-state.service";
import { UnitCatalog } from "./measurement.service";
import { FeatureFlagItem } from "source/app/features/feature-flag/feature-flag.models";
import {
	LocationOperationActivitiesTimeRange,
	LocationOperationActivity,
} from "source/app/features/activities/location-operation-activity.model";

@Injectable({
	providedIn: "root",
})
export class ApiService {
	constructor(
		private http: HttpClient,
		private environmentQuery: EnvironmentQuery,
		private loadingController: LoadingController,
		private locationStateQuery: LocationStateQuery,
		private translocoService: TranslocoService,
		private uiQuery: UiQuery,
	) {}

	private postToBackend = async <T>(endpoint: string, payload: object, headers?: HttpHeaders) => {
		const environment = await this.environmentQuery.environment$.firstAsync();
		return this.http
			.post<ApiResponseWithData<T>>(environment.api.url + endpoint, payload, { headers: headers })
			.pipe(map((response) => response.data))
			.firstAsync();
	};

	private getFromBackend = async <T>(endpoint: string, params: Record<string, string>, headers?: HttpHeaders) => {
		const environment = await this.environmentQuery.environment$.firstAsync();
		return this.http
			.get<ApiResponseWithData<T>>(environment.api.url + endpoint, { params, headers })
			.pipe(map((response) => response.data))
			.firstAsync();
	};

	unitCatalog = async (): Promise<UnitCatalog> => {
		return this.getFromBackend<UnitCatalog>("api/measurement/unit-catalog", null);
	};

	getFeatureFlags = async (): Promise<FeatureFlagItem[]> => {
		return this.getFromBackend<FeatureFlagItem[]>("api/feature-flags", {});
	};

	locationStatus = async (location: string): Promise<ServerStateResponse> => {
		return this.postToBackend<ServerStateResponse>("api/rideops/location-status", { location });
	};

	locationOperators = async (location: string): Promise<LocationOperatorsResponse | null> => {
		return this.getFromBackend<LocationOperatorsResponse | null>("api/rideops/location-operators", {
			location,
		});
	};

	testRideOpsKey = async (location: string, key: string): Promise<boolean | null> => {
		return this.getFromBackend<boolean | null>(
			"api/rideops/test-key",
			{ location },
			new HttpHeaders({ "X-RideOps-key": key }),
		);
	};

	getNotesSummary = async (page: number, waitForNonStaleResults = false) => {
		const pageSize = 15;
		const location = await this.locationStateQuery.location$
			.pipe(filter((loc): loc is Location => loc != null))
			.firstAsync();

		const request = {
			location: location.id,
			currentPage: page.toString(),
			pageSize: pageSize.toString(),
			waitForNonStaleResults: waitForNonStaleResults.toString(),
		};

		return this.getFromBackend<Paged<Note>>("api/rideops/note-summary", request);
	};

	getNoteDetails = async (note: string): Promise<NoteDetailsResponse> => {
		return this.getFromBackend<NoteDetailsResponse>("api/rideops/note-details", { note });
	};

	acknowledgeNoteHandover = async (note: string): Promise<boolean> =>
		this.showLoader(async () => {
			const location = await this.locationStateQuery.location$.firstAsync();
			await this.postToBackend("api/rideops/acknowledge-note-handover", {
				note,
				location: location.id,
			});
			return true;
		});

	getLocationActivities = async (
		location: string,
		timeRange: LocationOperationActivitiesTimeRange = "Past",
	): Promise<LocationOperationActivity[]> => {
		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC";
		return this.getFromBackend<LocationOperationActivity[]>("api/rideops/location/activities", {
			location,
			timeRange,
			timeZone,
		});
	};

	locationSummary = () => {
		return this.getFromBackend<LocationSummaryResult>("api/rideops/location-summary", {});
	};

	memberships = () => {
		return this.getFromBackend<string[]>("api/rideops/memberships", {});
	};

	assignmentDefinitionSettings = (): Promise<AssignmentDefinitionSettings> => {
		return this.getFromBackend<AssignmentDefinitionSettings>("api/rideops/assignments/definitions", {});
	};

	assignmentCategories = (): Promise<string[]> => {
		return this.getFromBackend<string[]>("api/rideops/assignments/categories", {});
	};

	getDelayedDispatchReasons = (): Promise<DelayedDispatchReasonResponse> => {
		return this.getFromBackend<DelayedDispatchReasonResponse>("api/rideops/dispatch-delay-reasons", {});
	};

	getEmptyDispatchReasons = (): Promise<EmptyDispatchReasonResponse> => {
		return this.getFromBackend<EmptyDispatchReasonResponse>("api/rideops/dispatch-empty-reasons", {});
	};

	assets = async (): Promise<AssetHierarchyResponse> =>
		this.showLoader(async () => {
			const location = await this.locationStateQuery.location$.firstAsync();
			return this.getFromBackend<AssetHierarchyResponse>("api/rideops/assets", {
				location: location.id,
			});
		});

	assignees = async (definition = ""): Promise<{ id: string; information: string; name: string }[]> =>
		this.showLoader(async () => {
			const location = await this.locationStateQuery.location$.firstAsync();
			return this.postToBackend<{ id: string; information: string; name: string }[]>(
				"api/rideops/assignments/assignees",
				{ targets: [location.id], definition },
			);
		});

	getCriticalResults = async (): Promise<CriticalResult[]> =>
		this.showLoader(async () => {
			const location = await this.locationStateQuery.location$.firstAsync();

			const parameters = {
				location: location.id,
				startOfDay: this.startOfDay(),
			};

			const reponse = await this.getFromBackend<CriticalResultsResponse>(
				"api/rideops/critical-results",
				parameters,
			);
			return reponse.items;
		});

	login = async (userId: string, pin: string): Promise<void> => {
		const location = await this.locationStateQuery.location$.firstAsync();

		const headers = new HttpHeaders({
			"X-User-Identifier": userId,
			"X-Pin-Code": pin,
		});

		await this.postToBackend("api/rideops/login", { location: location.id, user: userId, operation: 0 }, headers);
	};

	signInAttendant = async (attendant: string, position: number) =>
		this.showLoader(async () => {
			const location = await this.locationStateQuery.location$.firstAsync();

			await this.postToBackend("api/rideops/login", {
				location: location.id,
				user: attendant,
				position: position,
				operation: 0,
			});
		});

	signOutAttendant = async (attendant: string, position: number) =>
		this.showLoader(async () => {
			const location = await this.locationStateQuery.location$.firstAsync();

			await this.postToBackend("api/rideops/logout", {
				location: location.id,
				user: attendant,
				position: position,
				operation: 1,
			});
		});

	logout = async () =>
		this.showLoader(async () => {
			const location = await this.locationStateQuery.location$.firstAsync();
			const environment = await this.environmentQuery.environment$.firstAsync();
			const operator = await this.locationStateQuery.selectOperator$.firstAsync();
			const positions = await this.locationStateQuery.positions$.firstAsync();
			const hasAttendants = positions.some((position) => position.attendant != null);
			const endpoint = environment.api.url + "api/rideops/logout";

			const logoutRequest = {
				user: operator.id,
				location: location.id,
				when: new Date().toISOString(),
				operation: 1,
				signoutAllAttendants: hasAttendants,
			};

			await this.http.post(endpoint, logoutRequest).pipe(timeout(3000)).firstAsync();
		});

	open = () =>
		this.showLoader(async () => {
			const location = await this.locationStateQuery.location$.firstAsync();
			const payload: OpenLocationRequest = { location: location.id };
			await this.postToBackend("api/rideops/open-location", payload);
		});

	// `suppressLegacyDispatch`: asset rides already count each unit tap as a dispatch; do not also send
	// the legacy close-time catch-up (`dispatches`/`dispatchRequest`). Gate lives in
	// LocationStateService.closeLocation to avoid an ApiService → AssetDispatchService →
	// FeatureFlagService → ApiService DI cycle.
	close = (dispatchesAtClose: number, ridersAtClose: number, suppressLegacyDispatch = false) =>
		this.showLoader(async () => {
			const [operator, location, rideOpsSettings, carts, seats] = await combineLatest([
				this.locationStateQuery.operator$,
				this.locationStateQuery.location$,
				this.locationStateQuery.rideOpsSettings$,
				this.uiQuery.cartsInUse$,
				this.uiQuery.seatsUnavailable$,
			]).firstAsync();

			const dispatches = await this.locationStateQuery.dispatches$.firstAsync();
			const riders = await this.locationStateQuery.riders$.firstAsync();

			const extraDispatches = dispatchesAtClose - dispatches;
			const extraRiders = ridersAtClose - riders;

			const payload: CloseLocationRequest = suppressLegacyDispatch
				? { location: location.id }
				: {
						location: location.id,
						dispatches: extraDispatches < 0 ? 0 : extraDispatches,
						dispatchRequest: {
							location: location.id,
							when: new Date().toISOString(),
							riders: extraRiders < 0 ? 0 : extraRiders,
							operator: operator.id,
							dispatchUnitCapacity: rideOpsSettings.dispatchUnitCapacity,
							dispatchUnitsAvailable: carts,
							totalUnavailableCapacity: seats,
						},
					};

			await this.postToBackend("api/rideops/close-location", payload);
		});

	startDowntime = (
		description: string,
		categories: string[] = undefined,
		downtimeTemplate: string,
	): Promise<{ downtimeId: string }> =>
		this.showLoader(async () => {
			const location = await this.locationStateQuery.location$.firstAsync();

			const payload: StartDowntimeRequest = {
				location: location.id,
				downtimeDescription: description,
				downtimeCategories: categories,
				downtimeTemplate: downtimeTemplate,
			};

			return await this.postToBackend<{ downtimeId: string }>("api/rideops/start-downtime", payload);
		});

	resolveDowntime = async (): Promise<boolean> => {
		const downtime = await this.locationStateQuery.downtime$.firstAsync();

		if (!downtime) {
			return false;
		}

		await this.postToBackend(`api/rideops/resolve-downtime`, {
			downtime: downtime.id,
		});

		return true;
	};

	changePin = (pin: string) => {
		return this.postToBackend("api/rideops/change-pin-code", { pin: pin });
	};

	getStatistics = async (showLoader = true) => {
		const statisticsCall = async () => {
			const location = await this.locationStateQuery.location$.firstAsync();
			const operator = await this.locationStateQuery.operator$.firstAsync();

			const parameters = {
				location: location.id,
				timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
				startOfShift: operator.loggedIn,
			};

			return this.getFromBackend<LocationStatisticsResult>("api/rideops/location-statistics", parameters);
		};

		if (showLoader) {
			return this.showLoader(statisticsCall);
		}

		return statisticsCall();
	};

	getLibrary = () =>
		this.showLoader(async () => {
			const location = await this.locationStateQuery.location$
				.pipe(filter((loc): loc is Location => loc != null))
				.firstAsync();
			return this.getFromBackend<LibraryResponse>("api/rideops/library", { location: location.id });
		});

	getChecklist = (activity: LocationActivity) =>
		this.showLoader(async () => {
			const location = await this.locationStateQuery.location$.firstAsync();

			const parameters = {
				location: location.id,
				activity: activity,
			};

			return this.getFromBackend<Checklist>("api/rideops/required-checklist", parameters);
		});

	getExternalDispatch = async (source: string): Promise<ExternalDispatchEntry> => {
		const location = await this.locationStateQuery.location$.firstAsync();
		return this.getFromBackend<ExternalDispatchEntry>("api/rideops/external-dispatch", {
			location: location.id,
			source,
		});
	};

	getExternalQueueTime = async (source: string): Promise<ExternalQueueTimeEntry> => {
		const location = await this.locationStateQuery.location$.firstAsync();
		return this.getFromBackend<ExternalQueueTimeEntry>("api/rideops/external-queue-time", {
			location: location.id,
			source,
		});
	};

	uploadFiles$ = (files: File[], tags: string[] = []): Observable<HttpEvent<FileResponse>> => {
		const payload = new FormData();
		files.forEach((file, index) => payload.append(`file-${index}`, file));

		const environment$ = this.environmentQuery.environment$;
		return environment$.pipe(
			take(1),
			switchMap((environment) => {
				const endpoint = environment.api.url + "api/rideops/files";

				// ServiceWorker does currently not support reporting progress on uploads
				// https://stackoverflow.com/questions/62235319/reportprogress-is-not-working-in-production
				const headers =
					tags.length > 0
						? new HttpHeaders({ "ngsw-bypass": "", Tags: tags.join(",") })
						: new HttpHeaders({ "ngsw-bypass": "" });

				return this.http.post<FileResponse>(endpoint, payload, {
					reportProgress: true,
					observe: "events",
					headers,
				});
			}),
		);
	};

	private startOfDay = (): string => {
		const now = new Date();
		now.setHours(0, 0, 0, 0);
		return now.toISOString();
	};

	private _loader: HTMLIonLoadingElement;
	private _activeRequests = 0;
	private showLoader = async <T>(action: () => Promise<T>): Promise<T> => {
		this._activeRequests++;

		try {
			if (this._activeRequests === 1) {
				const message = this.translocoService.translate("LOADING_MESSAGE__PLEASE_WAIT");
				this._loader = await this.loadingController.create({ message: message });
				this._loader.present();
			}

			return await action();
		} finally {
			this._activeRequests--;
			if (this._activeRequests === 0) {
				this._loader?.dismiss();
			}
		}
	};
}

export interface LocationOperatorsResponse {
	id: string;
	name: string;
	language: string;
	scannerCode: string;
	state: "Open" | "Closed";
	barcodeSignIn: boolean;
	operators: Operator[];
	requiredCompetencies: DocumentReference[];
}

export interface OpenLocationRequest {
	location: string;
}

export interface CloseLocationRequest {
	location: string;
	createDowntime?: boolean;
	downtimeDescription?: string;
	downtimeCategories?: string[];
	downtimeTemplate?: string;
	dispatches?: number;
	dispatchRequest?: CreateDispatchRequest;
}

export interface StartDowntimeRequest {
	location: string;
	downtimeDescription?: string;
	downtimeCategories?: string[];
	downtimeTemplate?: string;
}

export interface CreateDispatchRequest {
	location: string;
	operator: string;
	when: string;
	riders: number;
	dispatchUnitCapacity: number;
	dispatchUnitsAvailable: number;
	totalUnavailableCapacity: number;
	dispatchDelayReason?: number;
}

export interface LocationSummaryResult {
	allowLocationSwitch: boolean;
	locations: Location[];
}

export interface CriticalResultsResponse {
	items: CriticalResult[];
}

export interface CriticalResult {
	result: string;
	checklistName: string;
	answered: string;
	answeredBy: string;
	asset?: string;
}

export interface DowntimeTemplate {
	id: string;
	name: string;
	description: string;
	color: string;
	categories: string[];
	assignmentConfigurationPolicyType?: assignmentConfigurationPolicyType;
}

export enum assignmentConfigurationPolicyType {
	Inherit = "Inherit",
	Create = "Create",
	DoNotCreate = "DoNotCreate",
}

export interface FileData {
	currentFilename: string;
	extension: string;
	fileRevision: number;
	fromCameraRoll: boolean;
	id: string;
	name: string;
	path: string;
	type: string;
}

export interface FileResponse {
	data: FileData[];
}

export type OperationalAssetType = "Unit" | "Section" | "Row" | "Seat";

export interface AssetHierarchyResponse extends DocumentReference {
	location: string;
	operationalType?: OperationalAssetType;
	nodes: AssetHierarchyResponse[];
}

export interface ExternalDispatchEntry {
	timestamp: string;
	tag: string;
	entry: {
		riders: number;
		utilization: number;
		dispatchUnitCapacity: number;
		dispatchUnitsAvailable: number;
		totalUnavailableCapacity: number;
		dispatchUnitCapacityAvailable: number;
	};
}

export interface ExternalQueueTimeEntry {
	timestamp: string;
	tag: string;
	entry: {
		queueTimeInMinutes: number;
		queueSize: number;
	};
}

export interface DelayedDispatchReasonResponse {
	items: DelayedDispatchReason[];
}

export interface DelayedDispatchReason {
	code: number;
	name: string;
}

export interface EmptyDispatchReasonResponse {
	items: EmptyDispatchReason[];
}

export interface EmptyDispatchReason {
	code: number;
	name: string;
}

export interface NoteDetailsResponse {
	id: string;
	title?: string;
	description: string;
	type: string;
	organization: string;
	requiresApproval: boolean;
	hasApproval: boolean;
	createdBy: DocumentReference;
	location: DocumentReference;
	attachments: Array<{ id: string; path: string; fromCameraRoll: boolean }>;
	isDeleted: boolean;
	isHandover: boolean;
	assignment?: { id: string };
	created: string;
	acknowledgedByCurrentUser?: boolean;
}
